import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Zap, 
  Activity,
  ArrowRight,
  ArrowLeft,
  Car,
  Building2,
  Search,
  Heart,
  Lock,
  Database,
  Wifi,
  Globe,
  Mail,
  Eye,
  EyeOff,
  Loader2,
  User,
  Phone,
  FileCheck,
  Fingerprint,
  Briefcase
} from 'lucide-react';
import { useExchangeAuth } from '../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

// =============================================================================
// PORTAL CONFIGURATIONS
// =============================================================================

const PORTAL_CONFIG = {
  driver: {
    title: "Driver Portal",
    subtitle: "Credential Vault Access",
    icon: Car,
    gradient: "from-neon-cyan via-blue-500 to-neon-purple",
    particleColor: "#06b6d4",
    theme: "road",
    phrases: [
      "Initializing secure connection...",
      "Verifying driver credentials...",
      "Loading your credential vault...",
      "Access granted. Welcome, Driver."
    ]
  },
  provider: {
    title: "Provider Portal",
    subtitle: "Healthcare Network Hub",
    icon: Building2,
    gradient: "from-neon-green via-emerald-500 to-teal-500",
    particleColor: "#22c55e",
    theme: "medical",
    phrases: [
      "Connecting to provider network...",
      "Authenticating organization...",
      "Syncing compliance data...",
      "Portal ready. Welcome, Provider."
    ]
  },
  broker: {
    title: "Broker Portal",
    subtitle: "Trust Network Command",
    icon: Briefcase,
    gradient: "from-neon-purple via-violet-500 to-pink-500",
    particleColor: "#8b5cf6",
    theme: "network",
    phrases: [
      "Establishing secure tunnel...",
      "Mapping trust relationships...",
      "Loading broker dashboard...",
      "Network active. Welcome, Broker."
    ]
  },
  patient: {
    title: "Patient Portal",
    subtitle: "Care Coordination Center",
    icon: Heart,
    gradient: "from-rose-500 via-pink-500 to-red-500",
    particleColor: "#f43f5e",
    theme: "care",
    phrases: [
      "Securing patient data...",
      "Connecting to care network...",
      "Loading your health profile...",
      "Ready for care. Welcome."
    ]
  }
};

// =============================================================================
// ANIMATION COMPONENTS
// =============================================================================

const WarpParticle = ({ delay, angle, color }) => {
  const distance = 800 + Math.random() * 400;
  const x = Math.cos(angle) * distance;
  const y = Math.sin(angle) * distance;
  
  return (
    <motion.div
      initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
      animate={{ x, y, opacity: [0, 1, 1, 0], scale: [0, 1, 2, 0] }}
      transition={{ duration: 1.5, delay, ease: "easeOut" }}
      className="absolute left-1/2 top-1/2 w-1 rounded-full"
      style={{
        height: 20 + Math.random() * 30,
        background: `linear-gradient(to bottom, transparent, ${color}, transparent)`,
        transform: `rotate(${angle}rad)`
      }}
    />
  );
};

const WarpField = ({ active, color, intensity = 100 }) => {
  const particles = Array.from({ length: intensity }, (_, i) => ({
    id: i,
    angle: (i / intensity) * Math.PI * 2 + Math.random() * 0.5,
    delay: Math.random() * 0.5
  }));
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      {active && particles.map(p => (
        <WarpParticle key={p.id} angle={p.angle} delay={p.delay} color={color} />
      ))}
    </div>
  );
};

const RoadAnimation = ({ active }) => {
  const [lines, setLines] = useState([]);
  
  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => {
      setLines(prev => [...prev.slice(-20), { id: Date.now(), x: Math.random() * 100 }]);
    }, 100);
    return () => clearInterval(interval);
  }, [active]);
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: active ? 1 : 0 }}
        className="absolute bottom-0 left-0 right-0 h-1/2"
        style={{ background: 'linear-gradient(to top, rgba(6, 182, 212, 0.3), transparent)' }}
      />
      {lines.map(line => (
        <motion.div
          key={line.id}
          initial={{ bottom: '50%', opacity: 0, scaleY: 0.1, left: `${line.x}%` }}
          animate={{ bottom: '-10%', opacity: [0, 1, 0], scaleY: 1 }}
          transition={{ duration: 0.8, ease: "easeIn" }}
          className="absolute w-1 h-20 bg-neon-cyan rounded-full"
          style={{ boxShadow: '0 0 20px #06b6d4' }}
        />
      ))}
    </div>
  );
};

const DNAHelix = ({ active }) => {
  const helixPoints = Array.from({ length: 20 }, (_, i) => i);
  
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      {active && helixPoints.map((point, i) => (
        <React.Fragment key={i}>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1, x: Math.sin(i * 0.5) * 100, y: (i - 10) * 30 }}
            transition={{ delay: i * 0.05, duration: 0.3, repeat: Infinity, repeatType: "reverse", repeatDelay: 1 }}
            className="absolute w-4 h-4 rounded-full bg-neon-green"
            style={{ boxShadow: '0 0 20px #22c55e' }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1, x: Math.sin(i * 0.5 + Math.PI) * 100, y: (i - 10) * 30 }}
            transition={{ delay: i * 0.05 + 0.1, duration: 0.3, repeat: Infinity, repeatType: "reverse", repeatDelay: 1 }}
            className="absolute w-4 h-4 rounded-full bg-emerald-400"
            style={{ boxShadow: '0 0 20px #34d399' }}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

const NetworkNodes = ({ active }) => {
  const nodes = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: Math.cos(i * (Math.PI * 2 / 12)) * 150,
    y: Math.sin(i * (Math.PI * 2 / 12)) * 150
  }));
  
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      {active && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="absolute w-20 h-20 rounded-full bg-neon-purple/30 border-2 border-neon-purple flex items-center justify-center z-10"
          style={{ boxShadow: '0 0 40px #8b5cf6' }}
        >
          <Globe className="w-10 h-10 text-neon-purple" />
        </motion.div>
      )}
      {active && nodes.map((node, i) => (
        <motion.div
          key={node.id}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1, x: node.x, y: node.y }}
          transition={{ delay: i * 0.1, duration: 0.5 }}
          className="absolute w-8 h-8 rounded-full bg-violet-500/50 border border-violet-400 flex items-center justify-center"
        >
          <div className="w-2 h-2 rounded-full bg-violet-300" />
        </motion.div>
      ))}
    </div>
  );
};

const HeartbeatAnimation = ({ active }) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      {active && (
        <>
          <svg className="absolute w-full h-32" viewBox="0 0 400 100">
            <motion.path
              d="M0,50 L80,50 L100,50 L110,20 L120,80 L130,30 L140,70 L150,50 L200,50 L220,50 L230,20 L240,80 L250,30 L260,70 L270,50 L320,50 L340,50 L350,20 L360,80 L370,30 L380,70 L390,50 L400,50"
              fill="none"
              stroke="#f43f5e"
              strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ filter: 'drop-shadow(0 0 10px #f43f5e)' }}
            />
          </svg>
          <motion.div
            animate={{ scale: [1, 1.2, 1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity, times: [0, 0.1, 0.2, 0.3, 1] }}
            className="absolute"
          >
            <Heart className="w-32 h-32 text-rose-500" fill="rgba(244, 63, 94, 0.3)" style={{ filter: 'drop-shadow(0 0 30px #f43f5e)' }} />
          </motion.div>
        </>
      )}
    </div>
  );
};

const BiometricScan = ({ active, onComplete }) => {
  const [scanProgress, setScanProgress] = useState(0);
  
  useEffect(() => {
    if (!active) { setScanProgress(0); return; }
    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) { clearInterval(interval); setTimeout(onComplete, 200); return 100; }
        return prev + 5;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [active, onComplete]);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? 1 : 0 }}
      className="absolute inset-0 flex items-center justify-center z-20"
    >
      <div className="relative w-48 h-48">
        <div className="absolute inset-0 border-4 border-neon-cyan/50 rounded-2xl">
          <div className="absolute top-0 left-0 w-8 h-8 border-l-4 border-t-4 border-neon-cyan rounded-tl-2xl" />
          <div className="absolute top-0 right-0 w-8 h-8 border-r-4 border-t-4 border-neon-cyan rounded-tr-2xl" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-l-4 border-b-4 border-neon-cyan rounded-bl-2xl" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-r-4 border-b-4 border-neon-cyan rounded-br-2xl" />
        </div>
        <motion.div
          animate={{ top: `${scanProgress}%` }}
          className="absolute left-0 right-0 h-1 bg-neon-cyan"
          style={{ boxShadow: '0 0 20px #06b6d4, 0 0 40px #06b6d4' }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <Fingerprint className="w-24 h-24 text-white/30" />
        </div>
        <div className="absolute -bottom-10 left-0 right-0 text-center">
          <span className="text-neon-cyan font-mono">{scanProgress}%</span>
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// INLINE AUTH FORM
// =============================================================================

const InlineAuthForm = ({ role, onBack, config }) => {
  const navigate = useNavigate();
  const { login, register } = useExchangeAuth();
  const [mode, setMode] = useState('register');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: '', company_name: '', smsConsent: false
  });

  const Icon = config.icon;
  const needsCompany = ['driver', 'provider', 'broker'].includes(role);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate(`/portal/${result.role || role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (formData.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setIsSubmitting(true);
    try {
      await register({ ...formData, role });
      toast.success('Account created! Welcome to OVA Xchange.');
      navigate(`/portal/${role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleLabels = {
    patient: 'Patient / Rider',
    driver: 'Driver',
    provider: 'Provider',
    broker: 'Broker'
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -20 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative z-10"
    >
      <button
        onClick={onBack}
        className="absolute top-6 left-6 flex items-center text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to OVA Xchange
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-8">
          {/* Role Icon */}
          <div className="flex justify-center mb-6">
            <motion.div
              animate={{ boxShadow: [`0 0 20px ${config.particleColor}40`, `0 0 40px ${config.particleColor}60`, `0 0 20px ${config.particleColor}40`] }}
              transition={{ duration: 2, repeat: Infinity }}
              className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center`}
            >
              <Icon className="w-8 h-8 text-white" />
            </motion.div>
          </div>

          <h2 className="text-2xl font-bold text-white text-center mb-2">
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="text-slate-400 text-center mb-6 text-sm">
            {mode === 'login' ? 'Sign in to your account' : 'Join the OVA Xchange'}
          </p>

          {/* Role Badge */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-3 px-4 py-2 rounded-lg bg-white/5 border border-white/10">
              <Icon className="w-5 h-5" style={{ color: config.particleColor }} />
              <span className="text-white text-sm">{roleLabels[role]} Account</span>
              <button onClick={onBack} className="text-xs text-slate-500 hover:text-white ml-2">Change</button>
            </div>
          </div>

          <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
            {mode === 'register' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">First Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text" name="first_name" value={formData.first_name} onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                      placeholder="John" required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Last Name</label>
                  <input
                    type="text" name="last_name" value={formData.last_name} onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                    placeholder="Smith" required
                  />
                </div>
              </div>
            )}

            {/* Company Name - Required for Driver, Provider, Broker */}
            {mode === 'register' && needsCompany && (
              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  {role === 'driver' ? 'Company / Fleet Name' : role === 'provider' ? 'Organization Name' : 'Brokerage Name'}
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text" name="company_name" value={formData.company_name} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                    placeholder={role === 'driver' ? 'ABC Transport LLC' : role === 'provider' ? 'Healthcare Services Inc.' : 'National NEMT Brokerage'}
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm text-slate-300 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email" name="email" value={formData.email} onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                  placeholder="you@company.com" required
                />
              </div>
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-sm text-slate-300 mb-1">Phone (Optional)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm text-slate-300 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange}
                  className="w-full pl-10 pr-12 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                  placeholder={mode === 'register' ? 'Min 8 characters' : '••••••••'} required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <motion.button
              type="submit" disabled={isSubmitting}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              className={`w-full py-3 rounded-lg font-semibold text-white bg-gradient-to-r ${config.gradient} disabled:opacity-50 flex items-center justify-center`}
            >
              {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (mode === 'login' ? 'Sign In' : 'Create Account')}
            </motion.button>
          </form>

          <div className="mt-6 text-center">
            <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-sm text-slate-400 hover:text-white">
              {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// =============================================================================
// BACKGROUND COMPONENTS
// =============================================================================

const DataStream = ({ delay = 0, duration = 3 }) => (
  <motion.div
    className="absolute h-px bg-gradient-to-r from-transparent via-neon-cyan to-transparent"
    style={{ width: '200px' }}
    initial={{ x: '-200px', opacity: 0 }}
    animate={{ x: '100vw', opacity: [0, 1, 1, 0] }}
    transition={{ duration, delay, repeat: Infinity, ease: 'linear' }}
  />
);

const Particle = ({ size = 4, delay = 0, x, y }) => (
  <motion.div
    className="absolute rounded-full bg-neon-cyan/30"
    style={{ width: size, height: size, left: `${x}%`, top: `${y}%` }}
    animate={{ opacity: [0.2, 0.8, 0.2], scale: [1, 1.5, 1] }}
    transition={{ duration: 3, delay, repeat: Infinity, ease: 'easeInOut' }}
  />
);

const IntegrationBadge = ({ icon: Icon, label, sublabel, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="flex flex-col items-center p-4 glass-card hover:border-neon-cyan/50 transition-all duration-300 group"
  >
    <div className="w-12 h-12 rounded-xl bg-neon-cyan/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
      <Icon className="w-6 h-6 text-neon-cyan" />
    </div>
    <span className="text-white font-medium text-sm">{label}</span>
    <span className="text-slate-500 text-xs">{sublabel}</span>
  </motion.div>
);

const PortalCard = ({ icon: Icon, title, description, color, role, delay, onEnter }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay, duration: 0.5 }}>
      <button
        onClick={() => onEnter(role)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`block w-full text-left p-6 glass-card hover:border-${color}/50 transition-all duration-300 group h-full relative overflow-hidden`}
      >
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: isHovered ? 0.15 : 0 }} className={`absolute inset-0 bg-gradient-to-br from-${color} to-transparent`} />
        <div className="relative z-10">
          <motion.div animate={{ scale: isHovered ? 1.1 : 1, rotate: isHovered ? 5 : 0 }} className={`w-14 h-14 rounded-2xl bg-${color}/20 flex items-center justify-center mb-4`}>
            <Icon className={`w-7 h-7 text-${color}`} />
          </motion.div>
          <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
          <p className="text-slate-400 text-sm mb-4">{description}</p>
          <span className={`inline-flex items-center text-sm text-${color}`}>Enter Portal <ArrowRight className="w-4 h-4 ml-1" /></span>
        </div>
      </button>
    </motion.div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const OVAXchangeLanding = () => {
  const [showContent, setShowContent] = useState(false);
  const [stage, setStage] = useState('landing'); // 'landing' | 'animating' | 'form'
  const [selectedRole, setSelectedRole] = useState(null);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handlePortalEnter = useCallback((role) => {
    setSelectedRole(role);
    setStage('animating');
    setAnimationPhase(0);
    setPhraseIndex(0);
  }, []);

  const handleBackToLanding = useCallback(() => {
    setStage('landing');
    setSelectedRole(null);
    setAnimationPhase(0);
    setPhraseIndex(0);
  }, []);

  // Animation sequence
  useEffect(() => {
    if (stage !== 'animating' || !selectedRole) return;

    const timers = [
      setTimeout(() => setAnimationPhase(1), 500),
      setTimeout(() => setAnimationPhase(2), 2000),
      setTimeout(() => setPhraseIndex(1), 2500),
      setTimeout(() => setPhraseIndex(2), 3500),
      setTimeout(() => setAnimationPhase(3), 4000),
      setTimeout(() => setPhraseIndex(3), 4200),
      setTimeout(() => setStage('form'), 5000)
    ];

    return () => timers.forEach(clearTimeout);
  }, [stage, selectedRole]);

  const config = selectedRole ? PORTAL_CONFIG[selectedRole] : null;

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i, x: Math.random() * 100, y: Math.random() * 100, size: Math.random() * 4 + 2, delay: Math.random() * 2
  }));

  const ThemedAnimation = config ? {
    road: RoadAnimation,
    medical: DNAHelix,
    network: NetworkNodes,
    care: HeartbeatAnimation
  }[config.theme] : null;

  return (
    <div className="min-h-screen bg-dark-navy relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 neural-bg opacity-30" />
        {[...Array(8)].map((_, i) => (
          <div key={i} style={{ position: 'absolute', top: `${10 + i * 12}%`, left: 0, right: 0 }}>
            <DataStream delay={i * 0.5} duration={4 + Math.random() * 2} />
          </div>
        ))}
        {particles.map(p => <Particle key={p.id} x={p.x} y={p.y} size={p.size} delay={p.delay} />)}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-navy via-transparent to-dark-navy" />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-navy/50 via-transparent to-dark-navy/50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-neon-cyan/5 rounded-full blur-3xl" />
      </div>

      {/* ANIMATION OVERLAY */}
      <AnimatePresence>
        {stage === 'animating' && config && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-50 bg-black"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: animationPhase >= 2 ? 0.3 : 0 }}
              className={`absolute inset-0 bg-gradient-to-br ${config.gradient}`}
            />
            
            {ThemedAnimation && <ThemedAnimation active={animationPhase >= 2} />}
            <WarpField active={animationPhase >= 2} color={config.particleColor} intensity={animationPhase >= 2 ? 150 : 0} />
            
            {animationPhase === 1 && <BiometricScan active={true} onComplete={() => {}} />}
            
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: animationPhase >= 3 ? 0 : animationPhase >= 2 ? 1.5 : 1, rotate: 0, opacity: animationPhase >= 3 ? 0 : 1 }}
                transition={{ type: "spring", damping: 15, stiffness: 100 }}
                className="relative z-30"
              >
                {[1, 2, 3].map(ring => (
                  <motion.div
                    key={ring}
                    initial={{ scale: 1, opacity: 0 }}
                    animate={{ scale: animationPhase >= 2 ? [1, 2, 3] : 1, opacity: animationPhase >= 2 ? [0.5, 0.3, 0] : 0 }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: ring * 0.3 }}
                    className="absolute inset-0 rounded-full border-2"
                    style={{ borderColor: config.particleColor }}
                  />
                ))}
                <motion.div
                  animate={{ boxShadow: animationPhase >= 2 ? `0 0 60px ${config.particleColor}, 0 0 120px ${config.particleColor}` : `0 0 30px ${config.particleColor}` }}
                  className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center`}
                >
                  {React.createElement(config.icon, { className: "w-16 h-16 text-white" })}
                </motion.div>
              </motion.div>
            </div>
            
            <div className="absolute bottom-32 left-0 right-0 text-center z-30">
              <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl font-bold text-white mb-2">
                {config.title}
              </motion.h2>
              <motion.p key={phraseIndex} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-lg font-mono" style={{ color: config.particleColor }}>
                {config.phrases[phraseIndex]}
              </motion.p>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: animationPhase >= 2 ? 1 : 0 }}
              className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center space-x-2 text-white/50 z-30"
            >
              <Shield className="w-4 h-4" />
              <span className="text-xs font-mono">SECURE CONNECTION ESTABLISHED</span>
              <Lock className="w-4 h-4" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FORM VIEW */}
      <AnimatePresence>
        {stage === 'form' && config && (
          <InlineAuthForm role={selectedRole} onBack={handleBackToLanding} config={config} />
        )}
      </AnimatePresence>

      {/* LANDING VIEW */}
      <AnimatePresence>
        {stage === 'landing' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12"
          >
            {/* Logo */}
            <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="mb-8">
              <motion.div
                animate={{ boxShadow: ['0 0 20px rgba(0, 245, 212, 0.3)', '0 0 60px rgba(0, 245, 212, 0.5)', '0 0 20px rgba(0, 245, 212, 0.3)'] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="p-8 rounded-3xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl border border-neon-cyan/30"
              >
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center">
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold">
                    <span className="text-white">OVA</span>
                    <span className="text-gradient ml-2">Xchange</span>
                  </h1>
                  <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ delay: 0.8 }} className="h-px bg-gradient-to-r from-transparent via-neon-cyan to-transparent mt-4" />
                </motion.div>
              </motion.div>
            </motion.div>

            {showContent && (
              <>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
                  <p className="text-xl sm:text-2xl text-slate-300 font-light mb-2">The Industry's Living Compliance Platform</p>
                  <p className="text-slate-500 text-sm font-mono">VERIFY ONCE • TRUST EVERYWHERE • ACT INSTANTLY</p>
                </motion.div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-3xl w-full">
                  <IntegrationBadge icon={Shield} label="Occuscreen" sublabel="Official Partner" delay={0.6} />
                  <IntegrationBadge icon={Activity} label="DMV Monitor" sublabel="Real-Time" delay={0.7} />
                  <IntegrationBadge icon={Database} label="Background" sublabel="Integrated" delay={0.8} />
                  <IntegrationBadge icon={Zap} label="Drug Screen" sublabel="TazAPI" delay={0.9} />
                </motion.div>

                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1 }} className="flex items-center space-x-2 mb-12 px-4 py-2 rounded-full bg-neon-green/10 border border-neon-green/30">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon-green opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-neon-green"></span>
                  </span>
                  <span className="text-neon-green text-sm font-mono">SYSTEM ACTIVE • MONITORING 500+ DRIVERS</span>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} className="w-full max-w-5xl">
                  <h2 className="text-center text-slate-400 text-sm font-mono uppercase tracking-widest mb-6">Select Your Portal</h2>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <PortalCard icon={Heart} title="Patient" description="Store insurance & medical docs for faster rides" color="neon-cyan" role="patient" onEnter={handlePortalEnter} delay={1.2} />
                    <PortalCard icon={Car} title="Driver" description="Manage your credentials, stay road-ready" color="neon-green" role="driver" onEnter={handlePortalEnter} delay={1.3} />
                    <PortalCard icon={Building2} title="Provider" description="Monitor your fleet's compliance in real-time" color="neon-purple" role="provider" onEnter={handlePortalEnter} delay={1.4} />
                    <PortalCard icon={Search} title="Broker" description="Instantly verify your provider network" color="neon-cyan" role="broker" onEnter={handlePortalEnter} delay={1.5} />
                  </div>
                </motion.div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="mt-12 text-center">
                  <p className="text-slate-500 text-sm mb-4">Powered by <span className="text-white">MedexOVA</span> • Integrated with <span className="text-neon-cyan">Occuscreen TazAPI</span></p>
                  <div className="flex items-center justify-center space-x-6 text-slate-600 text-xs">
                    <span className="flex items-center"><Lock className="w-3 h-3 mr-1" /> AES-256 Encrypted</span>
                    <span className="flex items-center"><Globe className="w-3 h-3 mr-1" /> Blockchain Verified</span>
                    <span className="flex items-center"><Wifi className="w-3 h-3 mr-1" /> Real-Time Sync</span>
                  </div>
                </motion.div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="mt-8">
                  <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">← Back to MedexOVA</Link>
                </motion.div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OVAXchangeLanding;
